/**
 * AdvisorChip — the trips header's way in to Shannon: her portrait, her name, and what pressing it
 * does (ELARA-750).
 *
 * It replaces both a "Chat with Shannon" button and the business-card treatment that stood beside
 * it. A button labelled with a person's name is a control that happens to mention her; the card was
 * a surface that competed with the header's own type for attention. Showing the portrait *as* the
 * affordance introduces the human first and reads as an action second, which is the point of
 * putting her in the header at all.
 *
 * Deliberately not `Button`: the standard variants are all sized and padded for a text label, and
 * every one of them frames the portrait in a box. This is a chip that hugs its content, so it gets
 * the base interactive contract by hand — pointer cursor, the shared focus ring, and a hover wash
 * that tracks the pill rather than a rectangle.
 */
import { cn } from '@elara/client-core/lib/utils';

import { ShannonAvatar } from '#views/trip/ShannonIntroDialog.js';

/** Big enough to read as a portrait rather than an icon, and to set the chip's height. */
const PORTRAIT_SIZE = 40;

export function AdvisorChip({ className, onPress }: { className?: string; onPress: () => void }) {
  return (
    <button
      type="button"
      onClick={onPress}
      // The visible text is a name and a role, so on its own it announces a person rather than what
      // activating this does.
      aria-label="Contact Shannon Culler, your travel advisor"
      className={cn(
        'group inline-flex items-center gap-3 cursor-pointer text-left',
        // The negative margin pulls the portrait back to the surrounding text's edge, so the chip's
        // own padding doesn't visually indent it. Mirrored from `lg`, where the chip sits at the
        // right of the header's action row with its portrait trailing (see the `order` classes
        // below) — the padding and the overhang swap sides with it.
        '-ml-1.5 rounded-full p-1.5 pr-5 lg:pr-1.5 lg:pl-5 lg:ml-0 lg:-mr-1.5',
        'transition-colors hover:bg-secondary',
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <ShannonAvatar size={PORTRAIT_SIZE} className="shrink-0 lg:order-2" />
      <span className="flex min-w-0 flex-col leading-tight lg:order-1 lg:text-right">
        {/* Leads with the verb, so the chip states what it does even without button chrome; her
            role goes underneath, where it introduces her rather than labelling the control. */}
        <span className="text-sm font-medium">Contact Shannon</span>
        <span className="text-xs text-muted-foreground">Your Elara travel advisor</span>
      </span>
    </button>
  );
}
